import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import { apiRequest, uploadFile } from "../utils/http";

const initialForm = {
  id: "",
  title: "",
  description: "",
  category: "",
  price: "",
  quantityAvailable: "",
  status: "available",
  isFeatured: false,
  isPublished: true,
  imageUrl: "",
  images: [],
  imagePublicId: ""
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImagePreview, setSelectedImagePreview] = useState("");

  const loadProducts = async () => {
    try {
      const [response, categoryResponse] = await Promise.all([
        apiRequest("/api/admin/products", { cache: "no-store" }),
        apiRequest("/api/admin/categories", { cache: "no-store" })
      ]);
      setProducts(response?.data || []);
      setCategories(categoryResponse?.data || []);
    } catch (loadError) {
      setError(loadError.message || "Unable to load products.");
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const openCreateDialog = () => {
    setForm(initialForm);
    setSelectedImagePreview("");
    setIsDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setForm({
      id: product._id,
      title: product.title || "",
      description: product.description || "",
      category: product.category || "",
      price: product.price ?? "",
      quantityAvailable: product.quantityAvailable ?? "",
      status: product.status || "available",
      isFeatured: Boolean(product.isFeatured),
      isPublished: product.isPublished !== false,
      imageUrl: product.imageUrl || "",
      images: [...new Set([product.imageUrl, ...(product.images || [])].filter(Boolean))],
      imagePublicId: product.imagePublicId || ""
    });
    setSelectedImagePreview(product.imageUrl || "");
    setIsDialogOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleUpload = async (event, slot = 0) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");
    setSuccess("");
    if (slot === 0) setSelectedImagePreview(URL.createObjectURL(file));

    try {
      setIsUploading(true);
      const response = await uploadFile("/api/uploads/image", file);
      setForm((current) => {
        const images = [...current.images];
        images[slot] = response.data.imageUrl;
        return { ...current, images, ...(slot === 0 ? { imageUrl: response.data.imageUrl, imagePublicId: response.data.imagePublicId } : {}) };
      });
      if (slot === 0) setSelectedImagePreview(response?.data?.imageUrl || "");
      setSuccess("Image uploaded successfully and ready for this listing.");
    } catch (uploadError) {
      setError(uploadError.message || "Unable to upload image.");
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.id && !form.category) { setError("Select an active rental category for the new product."); return; }
    setError("");
    setSuccess("");

    try {
      setIsSaving(true);
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        price: Number(form.price),
        quantityAvailable: Number(form.quantityAvailable),
        status: form.status,
        isFeatured: form.isFeatured === true || form.isFeatured === "true",
        isPublished: form.isPublished,
        imageUrl: form.imageUrl,
        imagePublicId: form.imagePublicId,
        images: [...new Set([form.imageUrl, ...form.images].filter(Boolean))]
      };

      const response = await apiRequest(
        form.id ? `/api/products/${form.id}` : "/api/products",
        {
          method: form.id ? "PUT" : "POST",
          body: JSON.stringify(payload)
        }
      );

      setSuccess(response?.message || (form.id ? "Product updated." : "Product created."));
      setIsDialogOpen(false);
      setForm(initialForm);
      setSelectedImagePreview("");
      await loadProducts();
    } catch (saveError) {
      setError(saveError.message || "Unable to save product.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusToggle = async (product) => {
    setError("");
    setSuccess("");

    try {
      const nextStatus = product.status === "available" ? "unavailable" : "available";
      const response = await apiRequest(`/api/products/${product._id}`, {
        method: "PUT",
        body: JSON.stringify({
          title: product.title,
          description: product.description,
          category: product.category,
          price: product.price,
          quantityAvailable: product.quantityAvailable,
          status: nextStatus,
          isFeatured: product.isFeatured,
          imageUrl: product.imageUrl,
          imagePublicId: product.imagePublicId,
          images: product.images || [product.imageUrl].filter(Boolean)
        })
      });
      setSuccess(response?.message || "Product status updated.");
      await loadProducts();
    } catch (statusError) {
      setError(statusError.message || "Unable to update product status.");
    }
  };

  const handleDelete = async (product) => {
    const confirmed = window.confirm(`Delete listing "${product.title}"?`);

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/products/${product._id}`, {
        method: "DELETE"
      });
      setSuccess(response?.message || "Product deleted.");
      await loadProducts();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete product.");
    }
  };

  const togglePublished = async product => {
    setError(""); setSuccess("");
    try {
      await apiRequest(`/api/products/${product._id}`, { method: "PUT", body: JSON.stringify({ isPublished: product.isPublished === false }) });
      setSuccess("Publication updated across the customer catalogue and Inventory.");
      await loadProducts();
    } catch (error) { setError(error.message); }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>Product Inventory</Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Create listings, update details, upload Cloudinary images, set availability, or remove old products.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDialog}>
          New Listing
        </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item xs={12} md={6} xl={4} key={product._id}>
            <Card sx={{ height: "100%" }}>
              <Box
                component="img"
                src={product.imageUrl}
                alt={product.title}
                sx={{ width: "100%", height: 240, objectFit: "cover" }}
              />
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{product.title}</Typography>
                    <Chip
                      label={product.status}
                      color={product.status === "available" ? "success" : "default"}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </Stack>

                  <Typography sx={{ color: "text.secondary", minHeight: 50 }}>
                    {product.description || "No description yet."}
                  </Typography>

                  <Typography sx={{ fontWeight: 800, color: "primary.main" }}>
                    NGN {Number(product.price || 0).toLocaleString()}
                  </Typography>

                  <Typography sx={{ color: "text.secondary" }}>
                    Available units: {product.quantityAvailable || 0}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                    <Button variant="outlined" onClick={() => openEditDialog(product)}>
                      Edit
                    </Button>
                    <Button variant="contained" color="warning" onClick={() => handleStatusToggle(product)}>
                      {product.status === "available" ? "Set Unavailable" : "Set Available"}
                    </Button>
                    <Button variant="text" color="error" onClick={() => handleDelete(product)}>
                      Delete
                    </Button>
                    <Button onClick={() => togglePublished(product)}>{product.isPublished === false ? "Publish" : "Unpublish"}</Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? "Update Listing" : "Create New Listing"}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSave}>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <TextField label="Title" name="title" value={form.title} onChange={handleChange} fullWidth />
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
              />
              <TextField select required={!form.id} label="Category" name="category" value={form.category} onChange={handleChange} fullWidth helperText="Rental categories only. Manage options in Categories. Existing legacy assignments are preserved.">
                <MenuItem value="" disabled={!form.id}>{form.id ? "Uncategorized (legacy)" : "Select a rental category"}</MenuItem>
                {categories.filter(c => c.active || c.key === form.category).map(c => <MenuItem key={c.key} value={c.key} disabled={!c.active}>{c.name}{!c.active ? " (inactive)" : ""}</MenuItem>)}
              </TextField>
              <TextField label="Price" name="price" type="number" value={form.price} onChange={handleChange} fullWidth />
              <TextField
                label="Quantity Available"
                name="quantityAvailable"
                type="number"
                value={form.quantityAvailable}
                onChange={handleChange}
                fullWidth
              />
              <TextField select label="Status" name="status" value={form.status} onChange={handleChange} fullWidth>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </TextField>
              <TextField select label="Published on both public sites" value={String(form.isPublished)} onChange={event => setForm(current => ({ ...current, isPublished: event.target.value === "true" }))} fullWidth>
                <MenuItem value="true">Published</MenuItem><MenuItem value="false">Unpublished</MenuItem>
              </TextField>
              <TextField
                select
                label="Featured"
                name="isFeatured"
                value={String(form.isFeatured)}
                onChange={(event) =>
                  setForm((current) => ({ ...current, isFeatured: event.target.value === "true" }))
                }
                fullWidth
              >
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
              </TextField>

              <Stack spacing={1.25}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadRoundedIcon />}
                  disabled={isUploading}
                >
                  {isUploading ? "Uploading..." : "Upload Product Image"}
                  <input hidden type="file" accept="image/*" onChange={handleUpload} />
                </Button>

                {selectedImagePreview && (
                  <Box
                    component="img"
                    src={selectedImagePreview}
                    alt="Uploaded product"
                    sx={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 2 }}
                  />
                )}

                <Typography sx={{ color: "text.secondary", fontSize: "0.92rem" }}>
                  {form.imageUrl
                    ? "Cloudinary image attached to this listing."
                    : "Select an image to preview it here, then it will upload to Cloudinary."}
                </Typography>
              </Stack>
              {[1, 2].map(slot => <Stack key={slot} spacing={1}>
                <Button component="label" variant="outlined" disabled={isUploading}>Upload gallery image {slot + 1}<input hidden aria-label={`Gallery image ${slot + 1}`} type="file" accept="image/*" onChange={event => handleUpload(event, slot)} /></Button>
                {form.images[slot] && <Box component="img" src={form.images[slot]} alt={`Gallery view ${slot + 1}`} sx={{ width: "100%", height: 120, objectFit: "contain" }} />}
              </Stack>)}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={isSaving || isUploading}>
            {isSaving ? "Saving..." : form.id ? "Update Product" : "Create Product"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ProductsPage;
